import { createListCollection, Portal, Select, Text } from '@chakra-ui/react';
import type React from 'react';

interface Option {
  value: string;
  label: string;
}

interface DropdownInputProps {
  label: string;
  value: string | string[];
  onChange: (value: string | string[]) => void;
  options: Option[];
  isRequired?: boolean;
  placeholder?: string;
  multiple?: boolean;
  width?: string;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
}

export const DropdownInput: React.FC<DropdownInputProps> = ({
  label,
  value,
  onChange,
  options,
  isRequired = false,
  placeholder = 'Select an option',
  multiple = false,
  width = '100%',
  size = 'md',
  disabled = false,
}) => {
  // Handle selection change
  const handleSelectionChange = (details: { value: string[] }) => {
    const newValues = details.value;
    if (multiple) {
      onChange(newValues);
    } else {
      // In single selection mode, return the first value or empty string
      onChange(newValues.length > 0 ? newValues[0] : '');
    }
  };

  // Convert value to the format expected by Select.Root
  const selectValue = Array.isArray(value) ? value : value ? [value] : [];

  const collection = createListCollection({
    items: options,
  });

  const frameworks = options.map((option) => ({
    value: option.value,
    label: option.label,
  }));

  return (
    <>
      <Select.Root
        multiple={multiple}
        collection={collection}
        width={width}
        value={selectValue}
        onValueChange={handleSelectionChange}
        size={size}
        disabled={disabled}
      >
        <Select.HiddenSelect />
        <Select.Label>
          {label}
          {isRequired && ' *'}
        </Select.Label>
        <Select.Control>
          <Select.Trigger>
            <Select.ValueText placeholder={placeholder} />
          </Select.Trigger>
          <Select.IndicatorGroup>
            <Select.Indicator />
          </Select.IndicatorGroup>
        </Select.Control>
        <Portal>
          <Select.Positioner>
            <Select.Content>
              {frameworks.map((option) => (
                <Select.Item item={option} key={option.value}>
                  {option.label}
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Portal>
      </Select.Root>

      {isRequired && (!value || (Array.isArray(value) && value.length === 0)) && (
        <Text color="red.500" fontSize="sm" mt={1}>
          Please select {multiple ? 'at least one option' : 'an option'}
        </Text>
      )}
    </>
  );
};
